/**
 * Role-based menu filtering utility
 * Filters menus based on user roles with manager/staff distinction
 */

export interface MenuItem {
  id: string;
  textKey: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItem[];
}

export interface UserRole {
  id?: number;
  name?: string;
  type?: string;
}

/**
 * Maps role names/types to their allowed menu sections.
 * Supports both display names (from Strapi role.name) and slug types (from Strapi role.type).
 * Dashboard and settings are always visible.
 */
const roleMenuMapping: { [key: string]: string[] } = {
  // Finance / Accountant – Finance menu
  'Accountant': ['finance'],
  'Accountant staff': ['finance'],
  'accountant': ['finance'],
  'accountant staff': ['finance'],
  'accountant-staff': ['finance'],

  // Admin – full access via isAdminRole; reports section
  'Admin': ['reports'],
  'Admin staff': ['reports'],
  'admin': ['reports'],
  'admin staff': ['reports'],
  'admin-staff': ['reports'],

  // Operations / Call Center – Operations menu
  'Call Center': ['operations'],
  'Call Center staff': ['operations'],
  'call center': ['operations'],
  'call center staff': ['operations'],
  'call-center': ['operations'],
  'call-center-staff': ['operations'],

  // HR – Organization (HR) menu
  'HR': ['organization'],
  'HR staff': ['organization'],
  'hr': ['organization'],
  'hr staff': ['organization'],
  'hr-staff': ['organization'],

  // Manager – organization/team access
  'Manager': ['organization'],
  'manager': ['organization'],

  // Employee – basic; can add sections as needed
  'Employee': ['organization'],
  'employee': ['organization'],

  // Sales – Sales & CRM menu
  'Sale': ['sales-crm'],
  'Sale staff': ['sales-crm'],
  'sale': ['sales-crm'],
  'sale staff': ['sales-crm'],
  'sale-staff': ['sales-crm'],
  'Sales': ['sales-crm'],
  'Sales staff': ['sales-crm'],
  'sales': ['sales-crm'],
  'sales-staff': ['sales-crm'],

  // Service provider – Operations (requests, etc.)
  'service-provider': ['operations'],
  'Service Provider': ['operations'],
};

/**
 * Check if a role is a staff role (contains "staff" in name)
 */
export const isStaffRole = (roleName: string | undefined): boolean => {
  if (!roleName) return false;
  return roleName.toLowerCase().includes('staff');
};

/**
 * Check if a role is a manager role (does not contain "staff")
 */
export const isManagerRole = (roleName: string | undefined): boolean => {
  if (!roleName) return false;
  return !isStaffRole(roleName);
};

/**
 * Get allowed menu sections for a role
 */
const getAllowedMenuSections = (roleName: string | undefined): string[] => {
  if (!roleName) return [];
  
  // Normalize role name (case-insensitive)
  const normalizedRoleName = Object.keys(roleMenuMapping).find(
    key => key.toLowerCase() === roleName.toLowerCase()
  );
  
  if (!normalizedRoleName) {
    // If role not found in mapping, return empty array (no access)
    return [];
  }
  
  return roleMenuMapping[normalizedRoleName] || [];
};

/**
 * Check if a role is admin (has full access)
 */
const isAdminRole = (roleName: string | undefined): boolean => {
  if (!roleName) return false;
  const normalized = roleName.toLowerCase();
  return normalized === 'admin' || 
         normalized === 'administrator' || 
         normalized.includes('admin') && !normalized.includes('staff');
};

/**
 * Check if a menu item should be visible for a role
 */
const isMenuItemVisible = (
  menuItem: MenuItem,
  roleName: string | undefined,
  isStaff: boolean
): boolean => {
  // Always show dashboard and settings
  if (menuItem.id === 'dashboard' || menuItem.id === 'settings') {
    return true;
  }
  
  // Admin roles can see all menus
  if (isAdminRole(roleName)) {
    return true;
  }
  
  // Get allowed sections for this role
  const allowedSections = getAllowedMenuSections(roleName);
  
  // Check if this menu section is allowed
  if (!allowedSections.includes(menuItem.id)) {
    return false;
  }
  
  return true;
};

/**
 * Get a single string to use for role-based menu lookup (tries name first, then type).
 */
const getRoleKeyForMenu = (userRole: UserRole | null | undefined): string | undefined => {
  if (!userRole) return undefined;
  const name = (userRole.name ?? (userRole as any)?.attributes?.name)?.toString()?.trim();
  const type = (userRole.type ?? (userRole as any)?.attributes?.type)?.toString()?.trim();
  return name || type || undefined;
};

/**
 * Filter menu items based on user role
 */
export const filterMenusByRole = (
  menuItems: MenuItem[],
  userRole: UserRole | null | undefined
): MenuItem[] => {
  const roleName = getRoleKeyForMenu(userRole);

  if (!userRole || !roleName) {
    // If no role, show only dashboard and settings
    return menuItems.filter(item => 
      item.id === 'dashboard' || item.id === 'settings'
    );
  }
  
  const isStaff = isStaffRole(roleName);
  
  // Filter menu items
  const filteredMenus = menuItems
    .filter(item => isMenuItemVisible(item, roleName, isStaff))
    .map(item => {
      // Create a deep copy to avoid mutating original
      const menuItemCopy: MenuItem = { 
        ...item,
        children: item.children ? [...item.children] : undefined
      };
      
      // Staff roles: hide overview and some report items for all staff
      if (isStaff && menuItemCopy.children && menuItemCopy.children.length > 0) {
        menuItemCopy.children = menuItemCopy.children.filter(child => {
          // No staff can see overview
          if (child.path?.includes('/overview')) {
            return false;
          }
          // Staff cannot see reports (except operations report)
          if (child.path?.includes('/reports') && menuItemCopy.id !== 'operations') {
            return false;
          }
          // Staff cannot see financial reports
          if (child.path?.includes('/financial-reports')) {
            return false;
          }
          // Staff cannot see tax report
          if (child.path?.includes('/finance/tax')) {
            return false;
          }
          return true;
        });
      }
      
      return menuItemCopy;
    })
    .filter(item => {
      // Remove parent menus that have no children left after filtering
      if (item.children && item.children.length === 0) {
        return false;
      }
      return true;
    });
  
  return filteredMenus;
};

